// The caster of a side and its four slots, in a match of Godsmode. Pure
// queries on the state. The flow has the actions.

import { HALF_STRENGTH_LOCK, ROYAL_BONUS } from '../constants';
import { type Caster, type CasterSkill, casterById } from '../content/casters';
import { pieceValue } from '../engine/evaluate';
import { livingCasterIds } from '../engine/roles';
import type { Board, Side } from '../engine/types';
import { spellById } from '../spells/library';
import type { SpellId } from '../spells/types';
import { energyOf, type GameState } from './state';

/** A slot of a caster. A locked skill waits for half strength. A scroll slot may be empty. */
export type Slot =
  | { kind: 'skill'; spellId: SpellId; cost: number; locked: boolean }
  | { kind: 'scroll'; spellId: SpellId | null };

/** The army is at half strength when its strength is this part of the start, or less. */
export const HALF_STRENGTH_RATIO = 0.5;

/** The value of the pieces of a side. A crown adds nothing: the measure is the pieces. */
export const armyStrength = (board: Board, side: Side): number =>
  board.reduce(
    (sum, piece) =>
      piece?.owner === side ? sum + pieceValue(piece) - (piece.royal ? ROYAL_BONUS : 0) : sum,
    0,
  );

export const isAtHalfStrength = (state: GameState, side: Side): boolean =>
  armyStrength(state.board, side) <= state.startStrength * HALF_STRENGTH_RATIO;

/** Does the skill wait for half strength? No skill waits while `HALF_STRENGTH_LOCK` is false. */
export const waitsForHalfStrength = (skill: CasterSkill): boolean =>
  HALF_STRENGTH_LOCK && skill.atHalfStrength === true;

/** The casters of the side, in the order of their slots. None in a match with a wheel. */
export const castersOf = (state: GameState, side: Side): Caster[] =>
  state.casters ? state.casters[side].map(casterById) : [];

/**
 * The slots of a caster: its skills, then its scroll slots. `atHalfStrength`
 * says whether its skills for half strength are unlocked. `scrolls` are the
 * scrolls it holds, in the order of its scroll slots.
 */
export const slotsOfCaster = (
  caster: Caster,
  atHalfStrength: boolean,
  scrolls: readonly SpellId[],
): Slot[] => {
  const skills: Slot[] = caster.skills.map((skill) => ({
    kind: 'skill',
    spellId: skill.spellId,
    cost: skill.cost,
    locked: waitsForHalfStrength(skill) && !atHalfStrength,
  }));
  const scrollSlots: Slot[] = Array.from({ length: caster.scrollSlots }, (_, index) => ({
    kind: 'scroll',
    spellId: scrolls[index] ?? null,
  }));
  return [...skills, ...scrollSlots];
};

/** The slots of a caster at the start of a match: no scroll, and no skill for half strength. The caster select shows them. */
export const openingSlots = (caster: Caster): Slot[] => slotsOfCaster(caster, false, []);

/**
 * A caster of a side, and whether a living piece of the side carries it. A
 * caster with no such piece casts nothing more and has no slot. Its plate
 * stays. Garrett, 2026-10-13: "the corpse remains but then can no longer
 * accrue spells, they are no longer in play". The scrolls in its slots go
 * with it, see `settleLostRoles` in `flow.ts`. Until then its plate was four
 * scroll slots, from Rob: "its \"corpse\" remains and becomes four scroll
 * slots".
 */
export type CasterGroup = { caster: Caster; alive: boolean };

/** The casters of the side, each with whether it lives. None in a match with a wheel. */
export const casterGroupsOf = (state: GameState, side: Side): CasterGroup[] => {
  const living = livingCasterIds(state.board, side);
  return castersOf(state, side).map((caster) => ({ caster, alive: living.has(caster.id) }));
};

/** The skills the caster still casts: all of them, or none when it is gone. */
const skillsOf = (group: CasterGroup): readonly CasterSkill[] =>
  group.alive ? group.caster.skills : [];

/** The scroll slots of the caster. A caster that is gone has none. */
const scrollSlotsOf = (group: CasterGroup): number => (group.alive ? group.caster.scrollSlots : 0);

/** The scroll slots of the living casters, over all of them. */
const livingScrollSlots = (groups: readonly CasterGroup[]): number =>
  groups.reduce((sum, group) => sum + scrollSlotsOf(group), 0);

/** The scroll slots of the side, over its living casters. */
export const scrollCapacity = (state: GameState, side: Side): number =>
  livingScrollSlots(casterGroupsOf(state, side));

/**
 * The slots of a side with these casters: the skills of each living caster in
 * turn, then the scroll slots of each living caster. `scrolls` are the scrolls
 * the side holds, in the order of the scroll slots.
 */
export const slotsOfCasters = (
  groups: readonly CasterGroup[],
  atHalfStrength: boolean,
  scrolls: readonly SpellId[],
): Slot[] => {
  const skills: Slot[] = groups.flatMap((group) =>
    skillsOf(group).map(
      (skill): Slot => ({
        kind: 'skill',
        spellId: skill.spellId,
        cost: skill.cost,
        locked: waitsForHalfStrength(skill) && !atHalfStrength,
      }),
    ),
  );
  const count = livingScrollSlots(groups);
  const scrollSlots: Slot[] = Array.from({ length: count }, (_, index) => ({
    kind: 'scroll',
    spellId: scrolls[index] ?? null,
  }));
  return [...skills, ...scrollSlots];
};

/** The slots of the side: its skills, then its scroll slots. Empty in a match with a wheel. */
export const slotsOf = (state: GameState, side: Side): Slot[] =>
  slotsOfCasters(casterGroupsOf(state, side), state.halfStrength[side], state.scrolls[side]);

/**
 * For each caster of a side, the places of its slots in the slots of the
 * side: its skills, then its scroll slots. A view that shows each caster on
 * its own reads a slot of the side by these, and casts by them. A caster that
 * is gone has no slot.
 */
export const slotIndexGroups = (groups: readonly CasterGroup[]): number[][] => {
  const skillCount = groups.reduce((sum, group) => sum + skillsOf(group).length, 0);
  let nextSkill = 0;
  let nextScroll = skillCount;
  return groups.map((group) => {
    const skills = skillsOf(group).map(() => nextSkill++);
    const scrolls = Array.from({ length: scrollSlotsOf(group) }, () => nextScroll++);
    return [...skills, ...scrolls];
  });
};

/**
 * The scrolls of a side, one list for each caster: the scrolls in its scroll
 * slots. `groups` are the casters as they were when the side held `scrolls`.
 */
export const scrollsByCaster = (
  groups: readonly CasterGroup[],
  scrolls: readonly SpellId[],
): SpellId[][] => {
  let next = 0;
  return groups.map((group) => {
    const held = scrolls.slice(next, next + scrollSlotsOf(group));
    next += scrollSlotsOf(group);
    return held;
  });
};

/** What the slot costs the side now. A scroll costs nothing. */
export const slotCost = (slot: Slot): number => (slot.kind === 'skill' ? slot.cost : 0);

/**
 * May the side to move cast from the slot now? It is before its move, the
 * slot holds a spell that is unlocked and paid for, and the spell has
 * something to aim at.
 */
export const canCastSlot = (state: GameState, index: number): boolean => {
  if (state.phase !== 'move' || state.mustMovePieceId !== null) return false;
  const side = state.turn;
  const slot = slotsOf(state, side)[index];
  if (!slot || slot.spellId === null) return false;
  if (slot.kind === 'skill' && (slot.locked || energyOf(state, side) < slot.cost)) return false;
  const spell = spellById(slot.spellId);
  if (!spell.canCast(state, side)) return false;
  return spell.targetCount === 0 || spell.eligible(state, side, []).length > 0;
};

export const castableSlots = (state: GameState): number[] =>
  slotsOf(state, state.turn).flatMap((_, index) => (canCastSlot(state, index) ? [index] : []));
